import {ChecklistItem} from "../../services/ReportingService";
import {getWorkflowLabel} from "../../atoms/start-workflow/workflowMapping";
import {reportingMenuGeneral, reportingMenuOther} from "./constants";
import {
  CHECKBOXES_URL_COMPONENT,
  getReportingSectionMeta,
  MenuItem,
  ReportingListCategory,
} from "./reporting-menu/helpers";
import {parseReportingListPath} from "./reporting-paths";

const LIST_CATEGORY_IN_PATH_PATTERN =
  /(?:^|\/)(general|other|checkboxes)\/([^/]+)/;

const LIST_CATEGORIES: ReadonlyArray<ReportingListCategory> = [
  "general",
  "other",
  CHECKBOXES_URL_COMPONENT,
];

const isListCategory = (value?: string): value is ReportingListCategory =>
  value === "general" ||
  value === "other" ||
  value === CHECKBOXES_URL_COMPONENT;

const staticListSectionById = new Map(
  [...reportingMenuGeneral, ...reportingMenuOther].map(item => [
    item.urlComponent,
    item.label,
  ]),
);

const FULL_REPORT_PATH_PATTERN =
  /(?:^|\/)reporting\/(?:analytics|reports)\/(general|other|checkboxes)\/([^/]+)\//;

const ANALYTICS_DASHBOARD_PATH_PATTERN =
  /(?:^|\/)analytics\/dashboards\/([^/]+)/;

const DASHBOARDS_URL_COMPONENT = "dashboards";

export type ReportingBreadcrumbLabels = Readonly<{
  workflowLabel: string;
  /** Present for nested menu groups (e.g. Checkboxes under a workflow). */
  submenuLabel?: string;
  reportLabel: string;
}>;

export type ReportingRouteParams = Readonly<{
  sectionId?: string;
  chartGroup?: string;
}>;

const CHECKLIST_LOADING_ITEM_ID = "loading";

const buildListBreadcrumb = (
  category: ReportingListCategory,
  sectionId: string,
  menu: ReadonlyArray<MenuItem>,
  selectedWorkflowLabel: string | null,
  checklistItems: ReadonlyArray<ChecklistItem>,
): ReportingBreadcrumbLabels | null => {
  const resolvedChecklistItems = checklistItems.filter(
    item => item.id !== CHECKLIST_LOADING_ITEM_ID,
  );
  const meta = getReportingSectionMeta(
    sectionId,
    category,
    resolvedChecklistItems,
  );
  const workflowLabel =
    selectedWorkflowLabel ??
    (meta ? getWorkflowLabel(meta.workflowType) : undefined);

  if (!workflowLabel) {
    return null;
  }

  const sectionLabel = findReportingSectionLabel(menu, category, sectionId);
  const checklistLabel = resolvedChecklistItems.find(
    item => item.id === sectionId,
  )?.description;
  const reportLabel =
    sectionLabel ??
    staticListSectionById.get(sectionId) ??
    checklistLabel ??
    meta?.label;

  if (!reportLabel) {
    return null;
  }

  const submenuLabel =
    category === CHECKBOXES_URL_COMPONENT
      ? findReportingCheckboxesSubmenuLabel(menu)
      : undefined;

  return {
    workflowLabel,
    ...(submenuLabel ? {submenuLabel} : {}),
    reportLabel,
  };
};

export const isReportingSectionActive = (
  pathname: string,
  category: ReportingListCategory,
  sectionId: string,
  routeParams?: ReportingRouteParams,
): boolean => {
  const context = resolveReportingListRouteContext(pathname, routeParams);

  return context?.category === category && context?.sectionId === sectionId;
};

export const resolveActiveCheckboxSectionId = (
  pathname: string,
  routeParams?: ReportingRouteParams,
): string | undefined => {
  const context = resolveReportingListRouteContext(pathname, routeParams);

  return context?.category === CHECKBOXES_URL_COMPONENT
    ? context.sectionId
    : undefined;
};

export const resolveReportingListRouteContext = (
  pathname: string,
  routeParams?: ReportingRouteParams,
): {category: ReportingListCategory; sectionId: string} | undefined => {
  const fromListPath = parseReportingListPath(pathname);

  if (fromListPath && isListCategory(fromListPath.category)) {
    return {category: fromListPath.category, sectionId: fromListPath.sectionId};
  }

  const segmentMatch = LIST_CATEGORY_IN_PATH_PATTERN.exec(pathname);

  if (segmentMatch && isListCategory(segmentMatch[1])) {
    return {category: segmentMatch[1], sectionId: segmentMatch[2]};
  }

  if (
    routeParams?.sectionId &&
    isListCategory(routeParams.chartGroup)
  ) {
    return {
      category: routeParams.chartGroup,
      sectionId: routeParams.sectionId,
    };
  }

  if (routeParams?.sectionId) {
    for (const category of LIST_CATEGORIES) {
      if (pathname.includes(`/${category}/`)) {
        return {category, sectionId: routeParams.sectionId};
      }
    }
  }

  return undefined;
};

const resolveAnalyticsDashboardBreadcrumb = (
  pathname: string,
  menu: ReadonlyArray<MenuItem>,
  routeParams?: ReportingRouteParams,
): ReportingBreadcrumbLabels | null => {
  const match = ANALYTICS_DASHBOARD_PATH_PATTERN.exec(pathname);
  const sectionId = match?.[1] ?? routeParams?.sectionId;

  if (!sectionId || !pathname.includes("/analytics/")) {
    return null;
  }

  const analyticsCategory = menu.find(
    item => item.urlComponent === "analytics",
  );
  const dashboardsGroup = analyticsCategory?.children?.find(
    group => group.urlComponent === DASHBOARDS_URL_COMPONENT,
  );
  const reportLabel = dashboardsGroup?.children?.find(
    section => section.urlComponent === sectionId,
  )?.label;

  if (!reportLabel) {
    return null;
  }

  return {
    workflowLabel: dashboardsGroup?.label ?? "Quicksight Dashboards",
    reportLabel,
  };
};

const findReportingCheckboxesSubmenuLabel = (
  menu: ReadonlyArray<MenuItem>,
): string => {
  const reportingCategory = menu.find(
    item => item.urlComponent === "reporting",
  );

  for (const workflow of reportingCategory?.children ?? []) {
    const checkboxesGroup = workflow.children?.find(
      node => node.urlComponent === CHECKBOXES_URL_COMPONENT,
    );

    if (checkboxesGroup) {
      return checkboxesGroup.label;
    }
  }

  return "Checkboxes";
};

/**
 * Walk the workflow-grouped menu to find a section leaf's label. Handles both
 * direct workflow children and the nested Checkboxes group.
 */
const findReportingSectionLabel = (
  menu: ReadonlyArray<MenuItem>,
  category: ReportingListCategory,
  sectionId: string,
): string | undefined => {
  const reportingCategory = menu.find(
    item => item.urlComponent === "reporting",
  );

  for (const workflow of reportingCategory?.children ?? []) {
    for (const node of workflow.children ?? []) {
      if (
        node.urlComponent === CHECKBOXES_URL_COMPONENT &&
        category === CHECKBOXES_URL_COMPONENT
      ) {
        const section = node.children?.find(
          item => item.urlComponent === sectionId,
        );

        if (section) {
          return section.label;
        }
      }

      if (node.listCategory === category && node.urlComponent === sectionId) {
        return node.label;
      }
    }
  }

  return undefined;
};

export const resolveReportingBreadcrumbLabels = (
  pathname: string,
  menu: ReadonlyArray<MenuItem>,
  selectedWorkflowLabel: string | null,
  routeParams?: ReportingRouteParams,
  checklistItems: ReadonlyArray<ChecklistItem> = [],
): ReportingBreadcrumbLabels | null => {
  const listRoute = resolveReportingListRouteContext(pathname, routeParams);

  if (listRoute) {
    return buildListBreadcrumb(
      listRoute.category,
      listRoute.sectionId,
      menu,
      selectedWorkflowLabel,
      checklistItems,
    );
  }

  const fullReportMatch = FULL_REPORT_PATH_PATTERN.exec(pathname);

  if (fullReportMatch && isListCategory(fullReportMatch[1])) {
    const [, subCategoryUrl, sectionId] = fullReportMatch;

    return buildListBreadcrumb(
      subCategoryUrl,
      sectionId,
      menu,
      selectedWorkflowLabel,
      checklistItems,
    );
  }

  if (routeParams?.sectionId && isListCategory(routeParams.chartGroup)) {
    return buildListBreadcrumb(
      routeParams.chartGroup,
      routeParams.sectionId,
      menu,
      selectedWorkflowLabel,
      checklistItems,
    );
  }

  const analyticsDashboardBreadcrumb = resolveAnalyticsDashboardBreadcrumb(
    pathname,
    menu,
    routeParams,
  );

  if (analyticsDashboardBreadcrumb) {
    return analyticsDashboardBreadcrumb;
  }

  return null;
};
